import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  Timestamp,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import './DepensePage.css';

// Aperçu d'une pièce jointe - gère PNG, JPEG, HEIC, WebP, etc. (fallback si format non supporté)
const AttachmentPreview = ({ file, onRemove }) => {
  const [previewError, setPreviewError] = useState(false);
  const objectUrl = file?.type?.startsWith('image/') ? URL.createObjectURL(file) : null;

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  if (!objectUrl || previewError) {
    return (
      <div className="preview-item preview-fallback">
        <div className="preview-file-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
        <span className="preview-filename">{file?.name || 'Image'}</span>
        <button type="button" className="preview-remove" onClick={onRemove} aria-label="Supprimer">×</button>
      </div>
    );
  }

  return (
    <div className="preview-item">
      <img src={objectUrl} alt={file?.name || 'Pièce'} onError={() => setPreviewError(true)} />
      <button type="button" className="preview-remove" onClick={onRemove} aria-label="Supprimer">×</button>
    </div>
  );
};

const DepensePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  const fileInputGalleryRef = useRef(null);
  const fileInputCameraRef = useRef(null);
  const { role: userRole } = useAdminRole();

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: '',
    attachments: []
  });
  const [editingExpense, setEditingExpense] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (!currentUser) {
        navigate('/admin');
      } else {
        loadExpenses();
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.depense-menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const expensesRef = collection(db, 'expenses');
      const q = query(expensesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = [];
      querySnapshot.forEach((docSnap) => {
        data.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      setExpenses(data);
    } catch (error) {
      console.error('Erreur chargement dépenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const uploadFiles = async (files, expenseId) => {
    const urls = [];
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0) || 1;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const prevBytes = files.slice(0, i).reduce((s, f) => s + f.size, 0);
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `expenses/${expenseId}/${Date.now()}_${i}.${ext}`;
      const storageRef = ref(storage, path);

      await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, file);
        task.on(
          'state_changed',
          (snapshot) => {
            const fileProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * file.size;
            const overall = ((prevBytes + fileProgress) / totalBytes) * 100;
            setUploadProgress(Math.round(Math.min(overall, 100)));
          },
          reject,
          async () => {
            const url = await getDownloadURL(storageRef);
            urls.push({ url, path });
            resolve();
          }
        );
      });
    }
    return urls;
  };

  const handleFileSelect = (e, fromCamera = false) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    const desc = formData.description.trim();
    const amount = parseFloat(formData.amount);
    if (!desc || isNaN(amount) || amount <= 0) {
      alert('Veuillez remplir la description et un montant valide (obligatoires)');
      return;
    }
    setSubmitting(true);
    setUploadProgress(0);
    try {
      const docRef = await addDoc(collection(db, 'expenses'), {
        description: desc,
        amount,
        date: formData.date || null,
        attachments: [],
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: Timestamp.now()
      });

      const uploaded = await uploadFiles(formData.attachments, docRef.id);
      await updateDoc(doc(db, 'expenses', docRef.id), {
        attachments: uploaded.map((u) => ({ url: u.url, path: u.path }))
      });

      setFormData({ description: '', amount: '', date: '', attachments: [] });
      await loadExpenses();
    } catch (error) {
      console.error('Erreur ajout dépense:', error);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount.toString(),
      date: expense.date || '',
      attachments: []
    });
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!user || !editingExpense) return;

    const desc = formData.description.trim();
    const amount = parseFloat(formData.amount);
    if (!desc || isNaN(amount) || amount <= 0) {
      alert('Veuillez remplir la description et un montant valide');
      return;
    }

    const hasNewFiles = formData.attachments.length > 0;

    setSubmitting(true);
    setUploadProgress(0);
    try {
      const prevData = {
        description: editingExpense.description,
        amount: editingExpense.amount,
        date: editingExpense.date || null,
        attachments: editingExpense.attachments || [],
        modifiedBy: editingExpense.modifiedBy || editingExpense.createdBy,
        modifiedByEmail: editingExpense.modifiedByEmail || editingExpense.createdByEmail,
        modifiedAt: editingExpense.modifiedAt || editingExpense.createdAt
      };

      let newAttachments = editingExpense.attachments || [];
      if (hasNewFiles) {
        const uploaded = await uploadFiles(formData.attachments, editingExpense.id);
        newAttachments = [...newAttachments, ...uploaded.map((u) => ({ url: u.url, path: u.path }))];
      }

      const modifications = [...(editingExpense.modifications || []), prevData];

      await updateDoc(doc(db, 'expenses', editingExpense.id), {
        description: desc,
        amount,
        date: formData.date || null,
        attachments: newAttachments,
        modifiedBy: user.uid,
        modifiedByEmail: user.email,
        modifiedAt: Timestamp.now(),
        modifications
      });

      setShowEditModal(false);
      setEditingExpense(null);
      setFormData({ description: '', amount: '', date: '', attachments: [] });
      await loadExpenses();
    } catch (error) {
      console.error('Erreur modification:', error);
      alert('Erreur lors de la modification');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;

    try {
      if (expense.attachments && expense.attachments.length > 0) {
        for (const att of expense.attachments) {
          try {
            const storageRef = ref(storage, att.path);
            await deleteObject(storageRef);
          } catch (e) {
            console.warn('Erreur suppression fichier:', e);
          }
        }
      }
      await deleteDoc(doc(db, 'expenses', expense.id));
      setOpenMenuId(null);
      await loadExpenses();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatAmount = (amount) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'TND', minimumFractionDigits: 2 }).format(amount);

  const formatDate = (ts) => (ts && ts.toDate ? ts.toDate().toLocaleString('fr-FR') : 'N/A');

  if (loading) {
    return (
      <div className="depense-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="depense-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />

      <div className="depense-content">
        <header className="depense-header">
          <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(true)} aria-label="Menu">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Dépenses</h1>
        </header>

        <main className="depense-main">
          <section className="depense-form-section">
            <h2>Nouvelle dépense</h2>
            <form onSubmit={handleSubmit} className="depense-form">
              <div className="form-group">
                <label htmlFor="description">Description <span className="required">*</span></label>
                <input
                  type="text"
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brève description de la dépense"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="amount">Montant (TND) <span className="required">*</span></label>
                <input
                  type="number"
                  id="amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div className="form-group attachments-group">
                <label>Factures / pièces jointes</label>
                <div className="attachment-buttons">
                  <button
                    type="button"
                    className="btn-attach btn-gallery"
                    onClick={() => fileInputGalleryRef.current?.click()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    Galerie (plusieurs images)
                  </button>
                  <button
                    type="button"
                    className="btn-attach btn-camera"
                    onClick={() => fileInputCameraRef.current?.click()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    Appareil photo
                  </button>
                </div>
                <input
                  ref={fileInputGalleryRef}
                  type="file"
                  accept="image/*,.png,.jpeg,.jpg,.jpe,.heic,.heif,.gif,.webp,.bmp,.tiff,.tif"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, false)}
                />
                <input
                  ref={fileInputCameraRef}
                  type="file"
                  accept="image/*,.png,.jpeg,.jpg,.jpe,.heic,.heif,.gif,.webp,.bmp,.tiff,.tif"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, true)}
                />
                {formData.attachments.length > 0 && (
                  <div className="attachment-previews">
                    {formData.attachments.map((file, i) => (
                      <AttachmentPreview key={i} file={file} onRemove={() => removeAttachment(i)} />
                    ))}
                  </div>
                )}
              </div>
              {submitting && (
                <div className="upload-progress-container">
                  <div className="upload-progress-bar">
                    <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <span className="upload-progress-text">{uploadProgress}%</span>
                </div>
              )}
              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? 'Enregistrement...' : 'Enregistrer la dépense'}
              </button>
            </form>
          </section>

          <section className="depense-list-section">
            <h2>Liste des dépenses</h2>
            {loadingExpenses ? (
              <div className="loading-container">
                <div className="spinner" />
                <p>Chargement...</p>
              </div>
            ) : expenses.length === 0 ? (
              <div className="empty-state">
                <p>Aucune dépense enregistrée</p>
              </div>
            ) : (
              <div className="expense-list">
                {expenses.map((exp) => (
                  <div key={exp.id} className="expense-card">
                    <div className="expense-card-body">
                      <p className="expense-description">{exp.description}</p>
                      <p className="expense-amount">{formatAmount(exp.amount)}</p>
                      {exp.date && <p className="expense-date">Date: {exp.date}</p>}
                      <div className="expense-meta">
                        <span>Créé par: {exp.createdByEmail || 'N/A'}</span>
                        <span>Le: {formatDate(exp.createdAt)}</span>
                      </div>
                      {exp.modifiedByEmail && (
                        <p className="expense-modified">
                          Modifié par: {exp.modifiedByEmail} le {formatDate(exp.modifiedAt)}
                        </p>
                      )}
                      {exp.attachments && exp.attachments.length > 0 && (
                        <div className="expense-attachments">
                          {exp.attachments.map((att, i) => (
                            <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="attachment-link">
                              Facture {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="depense-menu-container">
                      <button
                        className="menu-btn"
                        onClick={() => setOpenMenuId(openMenuId === exp.id ? null : exp.id)}
                        aria-label="Menu"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="1" />
                          <circle cx="12" cy="5" r="1" />
                          <circle cx="12" cy="19" r="1" />
                        </svg>
                      </button>
                      {openMenuId === exp.id && (
                        <div className="menu-dropdown">
                          <button className="menu-item" onClick={() => handleEdit(exp)}>
                            Modifier
                          </button>
                          {(exp.modifications || []).length > 0 && (
                            <button
                              className="menu-item"
                              onClick={() => {
                                setShowHistoryModal(exp);
                                setOpenMenuId(null);
                              }}
                            >
                              Historique des modifications
                            </button>
                          )}
                          <button className="menu-item delete-item" onClick={() => handleDelete(exp)}>
                            Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      {showEditModal && editingExpense && (
        <div className="modal-overlay" onClick={() => !submitting && (setShowEditModal(false), setEditingExpense(null))}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Modifier la dépense</h3>
            <form onSubmit={handleSaveEdit} className="depense-form">
              <div className="form-group">
                <label>Description <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Montant (TND) <span className="required">*</span></label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  step="0.01"
                  min="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div className="form-group attachments-group">
                <label>Factures (ajouter de nouvelles si besoin)</label>
                <p className="hint">Fichiers actuels conservés. Ajoutez-en pour compléter.</p>
                <div className="attachment-buttons">
                  <button type="button" className="btn-attach btn-gallery" onClick={() => fileInputGalleryRef.current?.click()}>
                    Galerie
                  </button>
                  <button type="button" className="btn-attach btn-camera" onClick={() => fileInputCameraRef.current?.click()}>
                    Appareil photo
                  </button>
                </div>
                {formData.attachments.length > 0 && (
                  <div className="attachment-previews">
                    {formData.attachments.map((file, i) => (
                      <AttachmentPreview key={i} file={file} onRemove={() => removeAttachment(i)} />
                    ))}
                  </div>
                )}
              </div>
              {submitting && (
                <div className="upload-progress-container">
                  <div className="upload-progress-bar">
                    <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <span className="upload-progress-text">{uploadProgress}%</span>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(null)}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Historique des modifications</h3>
            <p className="history-current">Actuel: {showHistoryModal.description} – {formatAmount(showHistoryModal.amount)}</p>
            <div className="history-list">
              {(showHistoryModal.modifications || []).slice().reverse().map((m, i) => (
                <div key={i} className="history-item">
                  <p><strong>Version précédente:</strong> {m.description} – {formatAmount(m.amount)}</p>
                  <p className="history-meta">
                    Modifié par {m.modifiedByEmail || 'N/A'} le {m.modifiedAt?.toDate ? m.modifiedAt.toDate().toLocaleString('fr-FR') : 'N/A'}
                  </p>
                </div>
              ))}
            </div>
            <button type="button" className="btn-cancel" onClick={() => setShowHistoryModal(null)}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepensePage;

